/**
 * BR-04 seven-step campaign wizard and BR-08 applications and offers
 * (FR-CAM-001..008, FR-PAR-002/003).
 *
 * The activation panel is the centre of this screen. FR-CAM-008 lists six
 * conditions and the server evaluates all of them in one function; this
 * renders that function's output verbatim rather than re-deciding anything,
 * so a brand is never told "approved, ready to go" by a screen while the
 * server is refusing on funding. That divergence is exactly what TC-007
 * exists to prevent, and the way to prevent it in the UI is to have no
 * second opinion to diverge with.
 */
import { useCallback, useEffect, useState, type ReactNode } from "react";
import { Button, Card, Eyebrow, PageHeader, StateBlock, Badge, Money } from "@ui/primitives";
import { FieldTip, Select, TextInput } from "@ui/forms";
import { fromMinorString } from "@contracts/money";
import { fontSize, space } from "@design/index";
import { api, type ApiResult } from "@app/shared/api";
import { useToast } from "@app/shared/Toasts";
import { DraftWizard } from "./DraftWizard";
import { formFromVersion } from "./draftForm";
import { FundingCard } from "./Funding";
import { VersionPreview } from "./VersionPreview";
import type { CoverRow } from "./CoverPhoto";
import { campaignCoverUrl } from "@app/shared/coverUrl";
import { BrandSubmissionsScreen, ConnectedAccountLink } from "./Submissions";
import { CampaignEarningsPanel } from "./Earnings";
import { platformMeta } from "@app/shared/Platforms";
import { CreatorAvatar } from "@app/shared/icons";

type CampaignTab = "setup" | "joined" | "submissions" | "earnings";

function parseCampaignTab(raw: string | null | undefined): CampaignTab {
  if (raw === "joined" || raw === "submissions" || raw === "earnings") return raw;
  return "setup";
}

interface ParticipantRow {
  id: string;
  creator_id: string;
  state: string;
  origin: string;
  gross_cap_minor: string;
  offered_at: string | null;
  expires_at: string | null;
  accepted_at: string | null;
  withdrawn_at: string | null;
  creator_name: string | null;
  avatar_href?: string | null;
  country: string | null;
  earned_minor?: string | null;
  connections?: Array<{ platform: string; handle: string | null; connection_state: string }> | null;
}

const messageOf = (r: Extract<ApiResult<unknown>, { ok: false }>) => r.message;
const minor = (v: string, ccy: string) => fromMinorString(v, ccy as "GBP" | "USD" | "EUR");
const asDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString(undefined,
    { day: "numeric", month: "short", year: "numeric" }) : "-";

interface CampaignRow {
  id: string; display_name: string; state: string; currency: string;
  version: number | null; review_state: string | null;
  cpm_gross_minor: string | null; join_closes_at: string | null;
  join_opens_at?: string | null; submission_deadline_at?: string | null;
  campaign_ends_at?: string | null;
  funded_minor: string; unallocated_minor: string; committed_minor: string;
  live_participants: number;
}

interface Gate { gate: string; passed: boolean; detail: string }

interface VersionRow {
  id: string; version: number; review_state: string;
  summary: string; objective: string | null; content_category: string | null;
  campaign_type: string | null; brief: Record<string, string>;
  eligibility: Record<string, unknown>; mode: string;
  cpm_gross_minor: string; per_creator_cap_minor: string; per_post_cap_minor: string | null;
  max_participants: number | null; submissions_per_creator: number; display_timezone: string;
  join_opens_at: string | null; join_closes_at: string | null;
  offer_expiry_hours: number; submission_deadline_at: string | null;
  campaign_ends_at: string | null;
  tracking_days: number; measurement_cutoff_at: string | null;
  min_live_days: number; earning_hold_days: number;
  measurement_routes: string[]; review_note: string | null;
  platform_terms?: Array<{ platform: string; budget_minor: number; cpm_gross_minor: number }>;
}

interface TransferTarget {
  id: string; display_name: string; state: string; currency: string;
}

interface CampaignFull {
  campaign: { id: string; display_name: string; state: string; currency: string;
              record_version: number };
  versions: VersionRow[];
  gates: Gate[];
  pool: Record<string, string> | null;
  covers?: CoverRow[];
  transfer_targets?: TransferTarget[];
}

const REVIEW_TONE: Record<string, "ok" | "warn" | "neutral" | "danger"> = {
  DRAFT: "neutral", IN_REVIEW: "warn", CHANGES_REQUESTED: "warn",
  APPROVED: "ok", REJECTED: "danger", ON_HOLD: "warn",
};

function reviewLabel(state: string): string {
  if (state === "APPROVED") return "Approved";
  return state.toLowerCase().replaceAll("_", " ");
}

function campaignStateLabel(state: string): string {
  if (state === "ACTIVE") return "Active";
  if (state === "PAUSED") return "Deactivated";
  if (state === "CLOSED") return "Closed";
  if (state === "CANCELLED") return "Cancelled";
  return "Inactive";
}

function CampaignRibbon({ state, review }: { state: string; review?: string | null }) {
  const live = state === "ACTIVE";
  const tone = live ? "ok"
    : state === "PAUSED" ? "warn"
    : state === "CANCELLED" ? "danger"
    : "neutral";
  return (
    <div style={{ display: "flex", gap: space(2), alignItems: "center", flexWrap: "wrap" }}>
      {review ? (
        <Badge tone={REVIEW_TONE[review] ?? "neutral"}>{reviewLabel(review)}</Badge>
      ) : null}
      <Badge tone={tone}>{campaignStateLabel(state)}</Badge>
    </div>
  );
}

const GATE_LABEL: Record<string, string> = {
  review_approved: "Reviewed and approved",
  funding_posted: "Funded",
  allocation_not_blocked: "No payment dispute",
  dates_valid: "Dates are valid",
  measurement_route_enabled: "Measurement route available",
  state_allows_activation: "Ready to activate",
  campaign_exists: "Campaign exists",
};

/* ------------------------------------------------------------------ list */

export function CampaignListScreen({ onOpen, startNew = false, onCancelNew, onStartNew }: {
  onOpen: (id: string) => void;
  startNew?: boolean;
  onCancelNew?: () => void;
  onStartNew?: () => void;
}) {
  const toast = useToast();
  const [rows, setRows] = useState<CampaignRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(startNew);
  const [name, setName] = useState("");
  const [creatingBusy, setCreatingBusy] = useState(false);

  const load = useCallback(async () => {
    const r = await api.get<{ campaigns: CampaignRow[] }>("/campaigns");
    if (r.ok) setRows(r.data.campaigns); else setError(messageOf(r));
  }, []);
  useEffect(() => { void load(); }, [load]);
  useEffect(() => { if (startNew) setCreating(true); }, [startNew]);

  const create = useCallback(async () => {
    setCreatingBusy(true);
    const r = await api.post<{ campaignId: string }>("/campaigns", {
      display_name: name, currency: "USD",
    });
    setCreatingBusy(false);
    if (r.ok) {
      toast.ok("Draft created.");
      onOpen(r.data.campaignId);
    } else {
      toast.error(messageOf(r));
      setError(messageOf(r));
    }
  }, [name, onOpen, toast]);

  if (error) return <StateBlock kind="error" title="Could not load campaigns" detail={error} />;
  if (rows === null) return <StateBlock kind="loading" title="Loading campaigns" />;

  return (
    <>
      <PageHeader
        eyebrow="Campaigns"
        title="Your campaigns"
        description="Draft a brief, send it for review, fund it, then activate. Nothing goes live until every gate passes."
        actions={<Button onClick={() => onStartNew ? onStartNew() : setCreating(true)}>New campaign</Button>}
      />

      {creating ? (
        <Card padding={20} style={{ display: "grid", gap: space(3), maxWidth: 520, marginBottom: space(3) }}>
          <div>
            <Eyebrow>New draft</Eyebrow>
            <p style={{ margin: `${space(1)} 0 0`, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.55 }}>
              A name. Campaigns are funded in USD. The brief, rate and dates come next.
            </p>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); if (name.trim().length >= 2) void create(); }}
                style={{ display: "grid", gap: space(3) }}>
            <TextInput
              label="Campaign name" required autoFocus value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              hint="Creators see this in discovery. You can rename it while it is a draft."
            />
            <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--faint)" }}>
              Currency · USD
            </p>
            <div style={{ display: "flex", gap: space(2), flexWrap: "wrap" }}>
              <Button type="submit" variant="primary" disabled={name.trim().length < 2 || creatingBusy}>
                {creatingBusy ? "Creating…" : "Create draft"}
              </Button>
              <Button type="button" variant="ghost" onClick={() => {
                setCreating(false);
                onCancelNew?.();
              }}>Cancel</Button>
            </div>
          </form>
        </Card>
      ) : null}

      {rows.length === 0 && !creating ? (
        <StateBlock
          kind="empty"
          title="No campaigns yet"
          detail="Create a draft to start. It stays private until you send it for review."
        />
      ) : (
        <div style={{ display: "grid", gap: space(3) }}>
          {rows.map((c) => (
            <Card key={c.id} padding={16}
                  style={{ display: "flex", justifyContent: "space-between",
                           gap: space(3), flexWrap: "wrap", alignItems: "center" }}>
              <div style={{ display: "grid", gap: 4, minWidth: 220 }}>
                <div style={{ fontSize: fontSize.bodyLg, fontWeight: 650 }}>{c.display_name}</div>
                <CampaignRibbon state={c.state} review={c.review_state} />
              </div>
              <div style={{ display: "flex", gap: space(5), flexWrap: "wrap",
                            fontSize: fontSize.label, color: "var(--muted)" }}>
                <Stat label="Funded" value={<Money value={minor(c.funded_minor, c.currency)} />} />
                <Stat label="Remaining" value={<Money value={minor(c.unallocated_minor, c.currency)} />} />
                <Stat label="Held for creators" value={<Money value={minor(c.committed_minor, c.currency)} />} />
                <Stat label="Creators" value={String(c.live_participants)} />
                <Stat label="Opens" value={asDate(c.join_opens_at ?? null)} />
                <Stat label="Submit by" value={asDate(c.submission_deadline_at ?? c.join_closes_at)} />
                <Stat label="Ends" value={asDate(c.campaign_ends_at ?? null)} />
              </div>
              <Button variant="secondary" size="sm" onClick={() => onOpen(c.id)}>Open</Button>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function Stat({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div style={{ display: "grid", gap: 2 }}>
      <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>{label}</span>
      <span style={{ fontSize: fontSize.body, color: "var(--text)" }}>{value}</span>
    </div>
  );
}

/* ---------------------------------------------------------------- detail */

export function CampaignEditorScreen({
  id, onBack, tab, onTab,
}: {
  id: string;
  onBack: () => void;
  tab?: string | null;
  onTab?: (tab: CampaignTab) => void;
}) {
  const toast = useToast();
  const [data, setData] = useState<CampaignFull | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [liveTotalMinor, setLiveTotalMinor] = useState<number | null>(null);
  const [counts, setCounts] = useState({ joined: 0, submissions: 0, earnings: 0 });
  const activeTab = parseCampaignTab(tab);

  const load = useCallback(async () => {
    const r = await api.get<CampaignFull>(`/campaigns/${id}`);
    if (r.ok) setData(r.data); else setError(messageOf(r));
    const [p, s, e] = await Promise.all([
      api.get<{ participants: ParticipantRow[] }>(`/campaigns/${id}/participants`),
      api.get<{ submissions: Array<{ campaign_id: string }> }>(`/submissions?state=ALL&campaign=${id}`),
      api.get<{ claims: Array<{ state: string }> }>(`/campaigns/${id}/earning-claims`),
    ]);
    const submissions = s.ok ? (s.data.submissions ?? []) : [];
    const scoped = /^[0-9a-f-]{36}$/i.test(id)
      ? submissions
      : submissions.filter((row) => row.campaign_id === id);
    const claims = e.ok ? (e.data.claims ?? []) : [];
    setCounts({
      joined: p.ok ? (p.data.participants ?? []).length : 0,
      submissions: scoped.length,
      earnings: claims.filter((c) => c.state === "REQUESTED").length,
    });
  }, [id]);
  useEffect(() => { void load(); }, [load]);

  const call = useCallback(async (path: string, body?: unknown, ok?: string) => {
    setBusy(true);
    const r = await api.post<Record<string, unknown>>(`/campaigns/${id}/${path}`, body);
    setBusy(false);
    if (r.ok) toast.ok(ok ?? "Saved.");
    else toast.error(messageOf(r));
    await load();
  }, [id, load, toast]);

  if (error) {
    return (
      <>
        <StateBlock kind="error" title="Could not load this campaign" detail={error} />
        <div style={{ marginTop: space(3) }}><Button variant="secondary" onClick={onBack}>Back</Button></div>
      </>
    );
  }
  if (!data) return <StateBlock kind="loading" title="Loading campaign" />;

  const latest = data.versions[0] ?? null;
  const draft = data.versions.find((v) => ["DRAFT", "CHANGES_REQUESTED"].includes(v.review_state));
  const ccy = data.campaign.currency;
  const ribbonReview = draft?.review_state ?? latest?.review_state ?? null;

  return (
    <div className="cm-brand-editor-page">
      <div>
        <Button variant="ghost" size="sm" onClick={onBack}>← Campaigns</Button>
      </div>

      <PageHeader
        eyebrow="Campaign"
        title={data.campaign.display_name}
        description="Everything a creator sees comes from the approved version. Edit campaign starts a new version and goes back through review."
        actions={<CampaignRibbon
          state={data.campaign.state}
          review={ribbonReview}
        />}
      />

      <div className="cm-dossier-tabs" role="tablist" aria-label="Campaign sections">
        {([
          { id: "setup" as const, label: "Campaign setup" },
          { id: "joined" as const, label: "Joined", count: counts.joined },
          { id: "submissions" as const, label: "Submissions", count: counts.submissions },
          { id: "earnings" as const, label: "Earnings", count: counts.earnings },
        ]).map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            id={`campaign-tab-${t.id}`}
            aria-selected={activeTab === t.id}
            aria-controls={`campaign-panel-${t.id}`}
            onClick={() => onTab?.(t.id)}
            className="cm-dossier-tab"
          >
            {t.label}
            {t.count != null ? <span className="cm-dossier-tab-count">{t.count}</span> : null}
          </button>
        ))}
      </div>

      {activeTab === "joined" ? (
        <div id="campaign-panel-joined" role="tabpanel" aria-labelledby="campaign-tab-joined">
          <JoinedCreatorsPanel campaignId={id} currency={ccy} />
        </div>
      ) : null}

      {activeTab === "submissions" ? (
        <div id="campaign-panel-submissions" role="tabpanel" aria-labelledby="campaign-tab-submissions">
          <BrandSubmissionsScreen campaignId={id} embedded currency={ccy} />
        </div>
      ) : null}

      {activeTab === "earnings" ? (
        <div id="campaign-panel-earnings" role="tabpanel" aria-labelledby="campaign-tab-earnings">
          <CampaignEarningsPanel campaignId={id} currency={ccy} />
        </div>
      ) : null}

      {activeTab === "setup" ? (
      <div id="campaign-panel-setup" role="tabpanel" aria-labelledby="campaign-tab-setup"
        className="cm-brand-editor">
        <div className="cm-brand-editor-form">
          {draft ? (
            <DraftWizard
              campaignId={id}
              displayName={data.campaign.display_name}
              recordVersion={data.campaign.record_version}
              version={draft}
              currency={ccy}
              onSaved={load}
              onSubmit={() => void call("submit", undefined, "Sent for review.")}
              submitBusy={busy}
              onLiveTotals={setLiveTotalMinor}
              cover={data.covers?.[0] ?? null}
              canDiscardDraft={data.versions.some((v) => v.review_state === "APPROVED")}
              discardBusy={busy}
              onDiscardDraft={() => {
                if (!window.confirm(
                  "Cancel this edit and restore the last approved version?",
                )) return;
                void call("draft/discard", undefined, "Edit cancelled. The last approved version is back.");
              }}
            />
          ) : latest ? (
            <SubmittedVersionCard
              displayName={data.campaign.display_name}
              version={latest}
              currency={ccy}
              forkBusy={busy}
              coverUrl={data.covers?.[0]?.id
                ? campaignCoverUrl(id, data.covers[0].id) : null}
              onFork={() => void call("versions", undefined, "Campaign is open to edit.")}
            />
          ) : (
            <Card padding={18} style={{ display: "grid", gap: space(2) }}>
              <Eyebrow>No version yet</Eyebrow>
              <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
                This campaign has no brief to show. Start a draft to write one.
              </p>
            </Card>
          )}

          {data.versions.filter((v) => v.review_note).map((v) => (
            <Card key={v.id} padding={18} style={{ display: "grid", gap: space(2) }}>
              <Eyebrow>Reviewer note on v{v.version} · {v.review_state.toLowerCase().replace("_", " ")}</Eyebrow>
              <p style={{ margin: 0, fontSize: fontSize.body, lineHeight: 1.6 }}>{v.review_note}</p>
            </Card>
          ))}
        </div>

        <div className="cm-brand-editor-aside">
          <ActivationPanel
            gates={data.gates}
            state={data.campaign.state}
            busy={busy}
            onActivate={() => void call("activate", undefined, "Campaign is live.")}
            unallocatedMinor={data.pool?.["unallocated_minor"] ?? "0"}
            currency={ccy}
            transferTargets={data.transfer_targets ?? []}
            onDeactivate={() => void call("deactivate", {}, "Campaign deactivated.")}
            onTransferLeftover={(transferTo) => void call(
              "transfer-leftover",
              { transfer_to: transferTo },
              "Leftover moved. Activate again after you pay the balance or receive a transfer.",
            )}
          />

          <FundingCard campaignId={id} currency={ccy}
                       campaignState={data.campaign.state}
                       onFunded={() => void load()}
                       liveTotalMinor={liveTotalMinor} />
        </div>
      </div>
      ) : null}
    </div>
  );
}

function JoinedCreatorsPanel({ campaignId, currency }: { campaignId: string; currency: string }) {
  const [rows, setRows] = useState<ParticipantRow[] | null | "error">(null);

  useEffect(() => {
    void api.get<{ participants: ParticipantRow[] }>(`/campaigns/${campaignId}/participants`)
      .then((r) => setRows(r.ok ? (r.data.participants ?? []) : "error"));
  }, [campaignId]);

  if (rows === null) return <StateBlock kind="loading" title="Loading joined creators…" />;
  if (rows === "error") return <StateBlock kind="error" title="Could not load the join list" />;
  if (rows.length === 0) {
    return (
      <StateBlock
        kind="empty"
        title="No one has joined yet"
        detail="Creators who accept an offer or join this campaign appear here, with their connected accounts and what they have earned."
      />
    );
  }

  return (
    <div style={{ display: "grid", gap: space(2) }}>
      {rows.map((p) => {
        const connections = Array.isArray(p.connections) ? p.connections : [];
        const earned = Number(p.earned_minor ?? 0);
        return (
          <Card key={p.id} padding={16} style={{ display: "grid", gap: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                <CreatorAvatar src={p.avatar_href} name={p.creator_name ?? undefined} size={40} />
                <div style={{ display: "grid", gap: 4 }}>
                <strong style={{ fontSize: fontSize.bodyLg }}>{p.creator_name ?? "A creator"}</strong>
                <span style={{ fontSize: fontSize.micro, color: "var(--faint)" }}>
                  {p.origin.replaceAll("_", " ").toLowerCase()}
                  {p.country ? ` · ${p.country}` : ""}
                  {p.accepted_at ? ` · joined ${asDate(p.accepted_at)}` : ""}
                </span>
                </div>
              </div>
              <Badge tone={p.state === "LIVE" ? "ok" : p.state === "WITHDRAWN" ? "danger" : "neutral"}>
                {p.state.replaceAll("_", " ").toLowerCase()}
              </Badge>
            </div>
            <div style={{ display: "flex", gap: 18, flexWrap: "wrap", fontSize: fontSize.body }}>
              <span>Cap <Money value={minor(p.gross_cap_minor, currency)} /></span>
              <span>
                Earned{" "}
                {earned > 0
                  ? <Money value={fromMinorString(String(earned), currency as "GBP" | "USD" | "EUR")} />
                  : <span style={{ color: "var(--faint)" }}>none yet</span>}
              </span>
            </div>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              {connections.length === 0 ? (
                <span style={{ fontSize: fontSize.label, color: "var(--muted)" }}>
                  No connected accounts
                </span>
              ) : connections.map((sc) => (
                <span key={`${p.id}-${sc.platform}`} style={{ fontSize: fontSize.label }}>
                  <ConnectedAccountLink platform={sc.platform} handle={sc.handle} />
                  <span className="cm-sr-only">{platformMeta(sc.platform).label}</span>
                </span>
              ))}
            </div>
          </Card>
        );
      })}
    </div>
  );
}

function submittedCopy(review: string, version: number): {
  eyebrow: string; description: string; canFork: boolean;
} {
  switch (review) {
    case "IN_REVIEW":
      return {
        eyebrow: `Version ${version} is with our reviewers`,
        description: "You will get an email when there is a decision. You can edit the campaign once this one is decided. The brief below is what we have.",
        canFork: false,
      };
    case "APPROVED":
      return {
        eyebrow: `Version ${version} is approved`,
        description: `This is the brief creators will see. Editing it would change terms they have already accepted, so Edit campaign starts version ${version + 1} and goes back through review.`,
        canFork: true,
      };
    case "REJECTED":
      return {
        eyebrow: `Version ${version} was rejected`,
        description: "This version did not pass review. Read the reviewer note, then edit the campaign to change the brief and send it again.",
        canFork: true,
      };
    case "ON_HOLD":
      return {
        eyebrow: `Version ${version} is on hold`,
        description: "Review is paused. You will get an email when there is a decision. The brief below is what we have.",
        canFork: false,
      };
    default:
      return {
        eyebrow: `Version ${version}`,
        description: "There is no open draft. The brief below is the latest version we have.",
        canFork: true,
      };
  }
}

function SubmittedVersionCard({ displayName, version, currency, forkBusy, onFork, coverUrl }: {
  displayName: string;
  version: VersionRow;
  currency: string;
  forkBusy: boolean;
  coverUrl?: string | null;
  onFork: () => void;
}) {
  const copy = submittedCopy(version.review_state, version.version);
  const form = formFromVersion(displayName, version);
  return (
    <Card padding={22} style={{ display: "grid", gap: space(4) }}>
      <div style={{ display: "grid", gap: space(2) }}>
        <Eyebrow>{copy.eyebrow}</Eyebrow>
        <p style={{ margin: 0, fontSize: fontSize.body, color: "var(--muted)", lineHeight: 1.6 }}>
          {copy.description}
        </p>
        {copy.canFork ? (
          <div>
            <Button variant="secondary" disabled={forkBusy} onClick={onFork}>
              Edit Campaign
            </Button>
          </div>
        ) : null}
      </div>
      <VersionPreview form={form} currency={currency} coverUrl={coverUrl} />
    </Card>
  );
}

/** FR-CAM-008 rendered exactly as the server reports it. */
function ActivationPanel({
  gates, state, busy, onActivate, unallocatedMinor, currency,
  transferTargets, onDeactivate, onTransferLeftover,
}: {
  gates: Gate[]; state: string; busy: boolean;
  onActivate: () => void;
  unallocatedMinor: string;
  currency: string;
  transferTargets: TransferTarget[];
  onDeactivate: () => void;
  onTransferLeftover: (transferTo: string) => void;
}) {
  const failing = gates.filter((g) => !g.passed);
  const live = state === "ACTIVE";
  const ended = state === "CLOSED" || state === "CANCELLED";
  const canDeactivate = state === "APPROVED" || state === "ACTIVE";
  const leftover = Number(unallocatedMinor);
  const canMoveLeftover = state === "PAUSED" && Number.isFinite(leftover) && leftover > 0;
  const [transferTo, setTransferTo] = useState("");
  const sameCurrency = transferTargets.filter((t) => t.currency === currency);

  const leftoverTip = canMoveLeftover
    ? "Remaining budget can only move while this campaign is deactivated. After it leaves, activate again only once you pay that amount or receive a transfer from another campaign."
    : "There is no remaining budget to move. Pay the balance or receive a transfer before activating again.";

  return (
    <Card padding={18} style={{ display: "grid", gap: space(3) }}>
      <div style={{ display: "flex", alignItems: "center" }}>
        <Eyebrow>{ended ? "Campaign ended" : "Before this can go live"}</Eyebrow>
        <FieldTip text={
          ended
            ? "This campaign is closed. Remaining budget that was still here had to stay or be moved before it ended."
            : "Every gate is checked again when you activate. After leftover is moved, pay the balance or receive a transfer first."
        } />
      </div>
      <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "grid", gap: space(2) }}>
        {gates.map((g) => (
          <li key={g.gate} style={{ display: "flex", gap: space(2), alignItems: "center",
                                    fontSize: fontSize.body }}>
            <span aria-hidden style={{ color: g.passed ? "var(--ok)" : "var(--warn)" }}>
              {g.passed ? "✓" : "○"}
            </span>
            <span>{GATE_LABEL[g.gate] ?? g.gate}</span>
            {!g.passed ? <FieldTip text={g.detail} /> : null}
          </li>
        ))}
      </ul>

      {!live && !ended ? (
        <Button block onClick={onActivate} disabled={busy || failing.length > 0}>
          {busy ? "Working…" : "Activate"}
        </Button>
      ) : null}

      {canDeactivate ? (
        <div style={{ display: "grid", gap: space(2), paddingTop: space(1),
                      borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Eyebrow>Deactivate</Eyebrow>
            <FieldTip text="Pause this campaign. Remaining budget stays here until you move it. Activating again uses the leftover that is still on this brief." />
          </div>
          <Button block variant="secondary" disabled={busy} onClick={() => {
            if (!window.confirm("Deactivate this campaign?")) return;
            onDeactivate();
          }}>
            {busy ? "Working…" : "Deactivate campaign"}
          </Button>
        </div>
      ) : null}

      {canMoveLeftover ? (
        <div style={{ display: "grid", gap: space(2), paddingTop: space(1),
                      borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Eyebrow>Move leftover</Eyebrow>
            <FieldTip text={leftoverTip} />
          </div>
          <p style={{ margin: 0, fontSize: fontSize.label, color: "var(--text)" }}>
            Remaining to move: <Money value={minor(unallocatedMinor, currency)} />
          </p>
          <Select
            label="Move leftover to"
            required
            value={transferTo}
            onChange={(e) => setTransferTo(e.currentTarget.value)}
            hint="Same currency. The destination Add funds card will show this transfer."
          >
            <option value="">Choose a campaign</option>
            {sameCurrency.map((t) => (
              <option key={t.id} value={t.id}>
                {t.display_name} · {t.state === "PAUSED" ? "deactivated" : t.state.toLowerCase().replaceAll("_", " ")}
              </option>
            ))}
          </Select>
          <Button block variant="secondary" disabled={
            busy || !transferTo || sameCurrency.length === 0
          } onClick={() => {
            if (!window.confirm("Move the remaining budget to the other campaign?")) return;
            onTransferLeftover(transferTo);
          }}>
            {busy ? "Working…" : "Move leftover"}
          </Button>
        </div>
      ) : state === "PAUSED" ? (
        <div style={{ display: "grid", gap: space(2), paddingTop: space(1),
                      borderTop: "1px solid var(--line)" }}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <Eyebrow>Move leftover</Eyebrow>
            <FieldTip text={leftoverTip} />
          </div>
        </div>
      ) : null}
    </Card>
  );
}
